import * as m from "mithril";
import * as dayjs from 'dayjs';
import { MessageProvider } from "./src/context/AppMessages";
import { getBusTypeString, getIntlStringValue } from "./src/helpers/StringHelpers";
import MiniTripItineraryView from "./src/components/MiniTripItineraryView";
import CustomSvgIcon from "./src/components/CustomSvgIcon";
import { formatMoney } from "./src/helpers/MoneyFormatter";
import { OperatorDTO, TentativeBookingDTO } from "./src/apiclient/ApiDtos";
import { PickupDropOffDetailsComponent } from "./src/pages/common/pickupdropoffInfo/PickupDropOffInfoDetails";
import { OrderedSeatsComponent } from "./src/pages/pay/OrderedSeatComponent";
import { SubtotalDetailsComponent } from "./src/pages/pay/SubtotalDetailsComponent";

function tbRow(label: string, valueContent: any, valueContentAlignment?: "left" | "right") {
  let alignmentClass = (valueContentAlignment == "right") ? "text-right" : "";
  return m("tr", [
    m("td.text-muted px-0", { style: "width:150px;word-break:break-word;" }, label),
    m("td.px-0", { "class": "text-right", style: "word-break:break-word;" }, valueContent)
  ]
  );
}

export default function TentativeBookingSummary(): m.Component<{
  messageProvider: MessageProvider,
  operator: OperatorDTO,
  tentativeBooking: TentativeBookingDTO,
  showTravellerInfoUpdateAction?: boolean,
  onTravellerInfoUpdateRequested?: () => void
}> {
  let viewingDetails: boolean = false;
  return {

    view(vnode) {
      let tentativeBooking = vnode.attrs.tentativeBooking;
      let trip = vnode.attrs.tentativeBooking.trip;
      let traveller = vnode.attrs.tentativeBooking.traveller;
      let messageProvider = vnode.attrs.messageProvider;
      let currLang = messageProvider.getCurrentLang();

      let busTypeString = getBusTypeString(trip, currLang);
      const scheduleTimeFormat = "MMM D, hh:mm A";

      let hasPickupDropOffOrder = (tentativeBooking.pickUpOrder != null && tentativeBooking.pickUpOrder.lineItems.length > 0)
        || (tentativeBooking.dropOffOrder != null && tentativeBooking.dropOffOrder.lineItems.length > 0);

      return m("div.card",
        [
          m("div.p-3 d-flex justify-content-between", {
            onclick: () => {
              viewingDetails = !viewingDetails;
            }
          },
            m("div",
              m("div",
                getIntlStringValue(
                  vnode.attrs.tentativeBooking.trip.departure.location.name,
                  currLang
                ),
                m("span", " - "),
                getIntlStringValue(
                  vnode.attrs.tentativeBooking.trip.arrival.location.name,
                  currLang
                ),
                m("span", " | "),
                dayjs(vnode.attrs.tentativeBooking.trip.departure.scheduledAt).format(scheduleTimeFormat)
              ),
              m("small.text-muted",
                getIntlStringValue(
                  vnode.attrs.operator.name,
                  currLang
                ),
                m("span", " | "),
                m("span", tentativeBooking.seatOrder.seatSelections.length + " " + (tentativeBooking.seatOrder.seatSelections.length > 1 ? messageProvider.get("seats") : messageProvider.get("seat"))),
                m("span", " | "),
                m("span",
                  vnode.attrs.tentativeBooking.trip.isForForeigner ?
                    vnode.attrs.messageProvider.get("foreigner")
                    : vnode.attrs.messageProvider.get("local")
                ),
              ),

              m("div.mt-2 small",
                m("span.mr-2",
                  hasPickupDropOffOrder ?
                    messageProvider.get("total_seats_price_plus_pickup_dropoff_fee")
                    : messageProvider.get("total_seat_price")
                ),
                m("span.text-success.font-weight-bold ml-3",
                  formatMoney(tentativeBooking.subtotal)
                )
              )
            ),
            m("div", m("span.mr-2",
              m(CustomSvgIcon, {
                iconType: viewingDetails ? 'caret_arrow_up' : 'caret_arrow_down',
                themeCssClass: "svg-small svg-muted"
              })
            ),
            )
          ),
          viewingDetails ?
            m("div.card mx-2 mb-3",
              [
                m("div.card-body mt-0", [
                  m("div.font-weight-bold mt-1 mb-3",
                    messageProvider.get("section.trip_summary_title")
                  ),

                  m(MiniTripItineraryView, {
                    trip: vnode.attrs.tentativeBooking.trip,
                    messageProvider,
                    size: 'medium',
                    fill: true
                  }),

                  m("div.mt-4", [
                    m("div.text-muted", messageProvider.get("trip.route")),
                    m("div.mt-1", m("span", getIntlStringValue(trip.fullRouteName, currLang)))
                  ]),

                  m("div.mt-4", [
                    m("div.text-muted", messageProvider.get("operator.name")),
                    m("div.mt-1", [
                      m("span", getIntlStringValue(trip.operator.name, currLang)),
                      m("span", " | "),
                      m("span", getIntlStringValue(trip.busType, currLang)),
                    ])
                  ]),

                  !tentativeBooking.trip.isSeatSelectionAllowed ?
                    m("div.card-body border-top px-0 py-3 mt-3",
                      m(OrderedSeatsComponent, {
                        hideTotalTicketPrice: trip.hasPickUpService || trip.hasDropOffService,
                        isSeatingAuto: !tentativeBooking.trip.isSeatSelectionAllowed,
                        allowCollapse: false,
                        messageProvider: vnode.attrs.messageProvider,
                        seatSelections: tentativeBooking.seatOrder.seatSelections
                      })
                    ) :
                    m("div.card-body px-0 py-3 mt-3",
                      m(OrderedSeatsComponent, {
                        hideTotalTicketPrice: trip.hasPickUpService || trip.hasDropOffService,
                        isSeatingAuto: !tentativeBooking.trip.isSeatSelectionAllowed,
                        allowCollapse: false,
                        messageProvider: vnode.attrs.messageProvider,
                        seatSelections: tentativeBooking.seatOrder.seatSelections
                      })
                    ),

                  (
                    (tentativeBooking.pickUpOrder && tentativeBooking.pickUpOrder != null && tentativeBooking.pickUpOrder.lineItems.length > 0)
                    || (tentativeBooking.dropOffOrder && tentativeBooking.dropOffOrder != null && tentativeBooking.dropOffOrder.lineItems.length > 0)
                  ) &&
                  m("div.mt-3",
                    m(PickupDropOffDetailsComponent, {
                      messageProvider: vnode.attrs.messageProvider,
                      totalNumberOfSeats: tentativeBooking.seatOrder.numberOfSeats,
                      trip: tentativeBooking.trip,
                      pickUpOrder: tentativeBooking.pickUpOrder,
                      dropOffOrder: tentativeBooking.dropOffOrder
                    })
                  ),

                  hasPickupDropOffOrder &&
                  m("div.mt-3 mb-5",
                    m(SubtotalDetailsComponent, {
                      messageProvider: messageProvider,
                      pickUpOrder: tentativeBooking.pickUpOrder,
                      dropOffOrder: tentativeBooking.dropOffOrder,
                      seatOrder: tentativeBooking.seatOrder,
                      subtotal: tentativeBooking.subtotal
                    })
                  ),

                  m("div.font-weight-bold mt-4 mb-2",
                    m("div.d-flex justify-content-between", [
                      m("span", messageProvider.get("section.traveller_info_title")),
                      (vnode.attrs.showTravellerInfoUpdateAction) ?
                        m("a.btn btn-sm btn-outline-primary", {
                          href: "#",
                          onclick: (event) => {
                            event.preventDefault();
                            if (vnode.attrs.onTravellerInfoUpdateRequested) {
                              vnode.attrs.onTravellerInfoUpdateRequested();
                            }
                          }
                        }, messageProvider.get("review.update_traveller_info")) :
                        m("div", "")
                    ])
                  ),

                  m("table.table.table-borderless.table-striped1.table-sm",
                    m("tbody",
                      [
                        tbRow(messageProvider.get("traveller.name"), traveller.name),
                        tbRow(messageProvider.get("traveller.phone"), traveller.phoneNumber),
                        tbRow(messageProvider.get("traveller.email"),
                          (traveller.email) ? traveller.email :
                            m("span.text-muted", `(${messageProvider.get('traveller.no_fill')})`)
                        ),
                        //tbRow(messageProvider.get("traveller.special_request"), traveller.specialRequest),
                      ]
                    )
                  ),

                  m("div.font-weight-bold mt-4 mb-2",
                    messageProvider.get("traveller.special_request")
                  ),
                  (traveller.specialRequest) ?
                    m("div.mt-2", traveller.specialRequest) :
                    m("div.text-muted", `(${messageProvider.get('traveller.no_fill')})`)
                ]),


              ]
            ) : null,

          /*
          m("div.card mt-3",
            [
              m("div.card-body", [
                m("div.font-weight-bold mt-1 mb-3",
                  messageProvider.get("section.due_amount_details")
                ),
                m("table.table.table-borderless.table-sm",
                  m("tbody",
                    [
                      tbRow(messageProvider.get("booking.subtotal_amount"), formatMoney(vnode.attrs.tentativeBooking.subtotal, false), "right"),
                      tbRow(messageProvider.get("booking.tax_amount"), "0", "right"),
                      tbRow(messageProvider.get("booking.discount_amount"), "0", "right"),
                    ]
                  )
                )
              ])              
            ]
          ),*/
        ]
      )
    }
  }
}